import { tr } from '@/lib/i18n-core';
import { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquareReply, Paperclip, X } from 'lucide-react';
import { COMMUNICATION_MAX_ATTACHMENTS, type CommunicationDto, type ReplyTemplateDto } from '@his/shared';
import { del, get, post, uploadWithProgress } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize, relative } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Badge, Button, Field, FormSkeleton, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { ExchangeBadge } from './insurance-status';
import { insuranceTabPath } from './insurance-routes';

const PRIORITY_VARIANT: Record<string, 'destructive' | 'warning' | 'secondary'> = { stat: 'destructive', asap: 'destructive', urgent: 'warning', routine: 'secondary' };

function formatSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ReplyPage() {
  const { requestId = '' } = useParams<{ requestId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('requests'));
  const item = useQuery({ queryKey: ['nphies-communication', requestId], queryFn: () => get<CommunicationDto>(`/integrations/nphies/communications/${requestId}`) });

  if (item.isLoading) return <FormSkeleton />;
  if (!item.data) return <Alert variant="destructive">{item.error ? describeError(item.error) : 'This request is not on file.'}</Alert>;

  return <ReplyEditor key={item.data.id} item={item.data} returnTo={returnTo} />;
}

function ReplyEditor({ item, returnTo }: { item: CommunicationDto; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const outbound = item.direction === 'outbound';
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState('');
  const [current, setCurrent] = useState<CommunicationDto>(item);
  const [uploading, setUploading] = useState(false);
  const answerable = current.status === 'open' && can('nphies.communication');
  const templates = useQuery({
    queryKey: ['nphies-reply-templates'],
    queryFn: () => get<ReplyTemplateDto[]>('/integrations/nphies/reply-templates'),
    enabled: answerable,
    staleTime: 5 * 60_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['nphies-communications'] });
    void queryClient.invalidateQueries({ queryKey: ['nphies-communication', item.id] });
    void queryClient.invalidateQueries({ queryKey: ['nphies-poll'] });
    void queryClient.invalidateQueries({ queryKey: ['claims'] });
    void queryClient.invalidateQueries({ queryKey: ['claim'] });
  };

  const reply = useMutation({
    mutationFn: () => post<{ status: string; message: string; queued: boolean; communication: CommunicationDto }>(`/integrations/nphies/communications/${item.id}/reply`, { text: text.trim() }),
    onSuccess: (result) => {
      setCurrent(result.communication);
      if (result.status === 'succeeded') toast.success('Reply sent', result.message);
      else if (result.queued) toast.warning('Saved for later', result.message);
      else toast.error('Could not send the reply', result.message);
      refresh();
      if (result.status === 'succeeded' || result.queued) navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not send the reply', describeError(error)),
  });

  const close = useMutation({
    mutationFn: () => post<CommunicationDto>(`/integrations/nphies/communications/${item.id}/close`, {}),
    onSuccess: () => {
      toast.success('Closed without a reply');
      refresh();
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not close it', describeError(error)),
  });

  const detach = useMutation({
    mutationFn: (attachmentId: string) => del<CommunicationDto>(`/integrations/nphies/communications/${item.id}/attachments/${attachmentId}`),
    onSuccess: (updated) => setCurrent(updated),
    onError: (error) => toast.error('Could not remove the file', describeError(error)),
  });

  const attach = async (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    setUploading(true);
    try {
      setCurrent(await uploadWithProgress<CommunicationDto>(`/integrations/nphies/communications/${item.id}/attachments`, form, () => undefined));
    } catch (error) {
      toast.error('Could not attach the file', describeError(error));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const ready = text.trim().length >= 3 && !uploading;
  const dirty = !reply.isSuccess && !close.isSuccess && text !== '';

  return (
    <FormScreen
      title={outbound ? (current.claimNumber ? `Message to the insurer about claim ${current.claimNumber}` : 'Message to the insurer') : current.claimNumber ? `Insurer request about claim ${current.claimNumber}` : 'Insurer request'}
      description={!outbound && current.dueAt ? `Answer by ${dateTime(current.dueAt)}` : undefined}
      icon={MessageSquareReply}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={
        answerable
          ? (event) => {
              event.preventDefault();
              if (ready) reply.mutate();
            }
          : undefined
      }
    >
      {!outbound && (
        <FormSection title={tr('billing', 'the_request')} columns={1}>
          <div className="text-sm">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <Badge variant={PRIORITY_VARIANT[current.priority] ?? 'secondary'}>{humanize(current.priority, 'priority')}</Badge>
              {current.subject && <span className="font-medium">{current.subject}</span>}
              {current.wantsAttachments && <Badge variant="warning">A file is asked for</Badge>}
              {current.overdue && <Badge variant="destructive">Past due</Badge>}
            </div>
            <p>{current.requestText ?? 'The insurer sent no text with this request.'}</p>
            <p className="mt-1 text-xs text-muted-foreground">Received {relative(current.receivedAt)}</p>
          </div>
        </FormSection>
      )}

      {current.status !== 'open' && (
        <Alert variant={current.replyState === 'failed' ? 'destructive' : 'default'}>
          {current.status === 'replied' ? `Answered ${current.repliedAt ? relative(current.repliedAt) : ''}` : `This request is ${current.status}.`}
          {current.replyState && (
            <span className="ms-2 inline-block align-middle">
              <ExchangeBadge state={current.replyState} />
            </span>
          )}
          {current.replyText && <p className="mt-1 whitespace-pre-wrap text-foreground">{current.replyText}</p>}
        </Alert>
      )}

      {answerable && (
        <>
          <FormSection title={tr('billing', 'your_reply')} icon={MessageSquareReply} columns={1}>
            {(templates.data ?? []).length > 0 && (
              <Field label="Start from a template" htmlFor="reply-template">
                <Select
                  id="reply-template"
                  value=""
                  onChange={(event) => {
                    const chosen = templates.data?.find((template) => template.id === event.target.value);
                    if (chosen) setText(chosen.body);
                  }}
                >
                  <option value="">Choose a template</option>
                  {(templates.data ?? []).map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={outbound ? 'Your message' : 'Your reply'} required hint={outbound ? 'Say what the insurer should know about this record. Attach a PDF or an image if it helps.' : 'Answer what the insurer asked. Attach a PDF or an image if a document is needed.'}>
              <Textarea rows={4} maxLength={2000} value={text} onChange={(event) => setText(event.target.value)} />
            </Field>
          </FormSection>
          <FormSection title={tr('billing', 'attachments')} icon={Paperclip} columns={1}>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileInput} type="file" accept="application/pdf,image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(event) => event.target.files?.[0] && void attach(event.target.files[0])} />
              <Button type="button" variant="outline" size="sm" className="gap-1.5" loading={uploading} disabled={current.attachments.length >= COMMUNICATION_MAX_ATTACHMENTS} onClick={() => fileInput.current?.click()}>
                <Paperclip className="h-3.5 w-3.5" /> Attach a file
              </Button>
              <span className="text-xs text-muted-foreground">Up to {COMMUNICATION_MAX_ATTACHMENTS} files of 5 MB. Files are kept in storage, not in the database.</span>
            </div>
            {current.attachments.map((file) => (
              <div key={file.id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
                <span className="font-medium">{file.fileName}</span>
                <span className="text-xs text-muted-foreground">{formatSize(file.sizeBytes)}</span>
                <Button type="button" size="icon" variant="ghost" className="ms-auto h-6 w-6" aria-label="Remove file" onClick={() => detach.mutate(file.id)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </FormSection>
          <FormActions
            submitLabel={outbound ? 'Send the message' : 'Send the reply'}
            loading={reply.isPending}
            incomplete={!ready}
            cancelTo={returnTo}
            secondary={
              <Button type="button" variant="ghost" onClick={() => close.mutate()} loading={close.isPending}>
                {outbound ? 'Discard' : 'Close without a reply'}
              </Button>
            }
          />
        </>
      )}
    </FormScreen>
  );
}
